import React, {useState, useEffect, useRef} from 'react';
import {connect} from 'react-redux';
import {Link, Redirect, useLocation} from 'react-router-dom';
import {Dropdown, Sidebar} from '../shared';
import {userInitials, roleBasedUrl} from '../../helpers';
import {logout, refreshUser} from '../../auth';
import {getPartnerPendingCounts} from '../../api/partnerApi';
import history from '../../history';
import swal from 'sweetalert';
import '../../i18n';
import {useTranslation, initReactI18next} from 'react-i18next';
import logo from '../../images/logo6.png';

const PartnerLayout = ({currentUser, children}) => {
	const loggedIn = currentUser && currentUser.email !== undefined;
	const [section, setSection] = useState('');
	const [pendingCounts, setPendingCounts] = useState({ group_tours: 0, shared_rentals: 0, vendor_services: 0 });
	const prevPendingCountsRef = useRef(null);
	const [showOnboardingBanner, setShowOnboardingBanner] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const location = useLocation();
	const {t, i18n} = useTranslation();

	// Only vendors with a vendor record can access the vendor portal; everyone else goes home
	if (loggedIn) {
		if (currentUser.role !== 'Vendor') return <Redirect to="/" />;
		// Role is Vendor but no vendor record (e.g. role changed in DB) → avoid blank dashboard
		if (!currentUser.vendor || !currentUser.vendor.id) return <Redirect to="/" />;
	}

	// Fetch latest user when entering partner so UI reflects server state (email/role/vendor changes)
	useEffect(() => {
		if (loggedIn) refreshUser();
	}, []);

	// Fetch pending assignment counts for sidebar badges (vendor_completed_at IS NULL)
	useEffect(() => {
		if (!loggedIn || !currentUser?.vendor?.id) return;
		getPartnerPendingCounts()
			.then((res) => {
				const d = res.data || {};
				const next = {
					group_tours: d.group_tours || 0,
					shared_rentals: d.shared_rentals || 0,
					vendor_services: d.vendor_services || 0
				};
				// Detect increase in vendor_services (new private tour assignment); show toast once, do not duplicate.
				const prev = prevPendingCountsRef.current;
				if (prev != null && next.vendor_services > prev.vendor_services) {
					swal('New assignment', 'You have a new private tour assignment.', 'info');
				}
				prevPendingCountsRef.current = next;
				setPendingCounts(next);
			})
			.catch(() => {});
	}, [loggedIn, currentUser?.vendor?.id, location.pathname]);

	// Tab title via Seo on partner routes; avoid fighting react-helmet on leave.
	useEffect(() => {
		document.title = 'Visit All Nepal – Vendor';
	}, []);

	// One-time welcome banner after completing /vendor/onboarding
	useEffect(() => {
		try {
			if (sessionStorage.getItem('vendor_onboarding_done') === '1') {
				sessionStorage.removeItem('vendor_onboarding_done');
				setShowOnboardingBanner(true);
			}
		} catch (_) {}
	}, []);

	useEffect(() => {
		const p = location.pathname || '';
		if (p.includes('group-trips')) setSection('my_group_trips');
		else if (p.includes('shared-rentals')) setSection('my_shared_rentals');
		else if (p.includes('private-tours')) setSection('my_private_tours');
		else if (p.includes('group_tours')) setSection('group_tours');
		else if (p.includes('shared_rentals')) setSection('shared_rentals');
		else if (p.includes('package_bookings')) setSection('packages');
		else if (p.includes('service_transactions')) setSection('transactions');
		else setSection((location.hash || '').replace('#', ''));
	}, [location.pathname, location.hash]);

	const assignmentTypes = (currentUser && currentUser.vendor && currentUser.vendor.assignment_types)
		? currentUser.vendor.assignment_types.split(',').map((s) => s.trim()).filter(Boolean)
		: [];
	const showAllTypes = assignmentTypes.length === 0;
	const canGroup = showAllTypes || assignmentTypes.includes('GroupTour');
	const canShared = showAllTypes || assignmentTypes.includes('SharedRental');
	const canPrivate = showAllTypes || assignmentTypes.includes('PartnerService');

	const groupPending = pendingCounts.group_tours || 0;
	const rentalPending = pendingCounts.shared_rentals || 0;
	const packagesPending = pendingCounts.vendor_services || 0;

	// Same object shape for all three so Sidebar renders badge identically (badgeVariant, ml-auto, badge-pill).
	const privateOrdersItem = canPrivate ? {
		icon: 'fas fa-briefcase',
		name: 'packages',
		label: 'Private tour orders',
		value: packagesPending > 0 ? String(packagesPending) : '',
		active: section == 'packages',
		badgeVariant: packagesPending > 0 ? 'danger' : null,
		title: packagesPending > 0 ? `${packagesPending} tour${packagesPending !== 1 ? 's' : ''} pending completion` : ''
	} : null;
	const groupOrdersItem = canGroup ? {
		icon: 'fas fa-route',
		name: 'group_tours',
		label: 'Group trip orders',
		value: groupPending > 0 ? String(groupPending) : '',
		active: section === 'group_tours',
		badgeVariant: groupPending > 0 ? 'danger' : null,
		title: groupPending > 0 ? `${groupPending} trip${groupPending !== 1 ? 's' : ''} pending completion` : ''
	} : null;
	const sharedOrdersItem = canShared ? {
		icon: 'fas fa-car-side',
		name: 'shared_rentals',
		label: 'Shared rental orders',
		value: rentalPending > 0 ? String(rentalPending) : '',
		active: section === 'shared_rentals',
		badgeVariant: rentalPending > 0 ? 'danger' : null,
		title: rentalPending > 0 ? `${rentalPending} trip${rentalPending !== 1 ? 's' : ''} pending completion` : '',
		show: false,
	} : null;

	const fullSideBar = [
		{ icon: 'fas fa-columns', name: '', label: 'Vendor Dashboard', value: '', active: section === '' },
		{
			label: 'My orders',
			name: 'orders',
			show: canPrivate || canGroup || canShared,
			children: [privateOrdersItem, groupOrdersItem, sharedOrdersItem].filter((item) => item && item.show !== false)
		},
		{
			label: 'My listings',
			name: 'services',
			show: false,
			children: [
				{ icon: 'fas fa-route', name: 'my_group_trips', label: 'Group trip listings', active: section === 'my_group_trips' },
				{ icon: 'fas fa-car-side', name: 'my_shared_rentals', label: 'Shared rental listings', active: section === 'my_shared_rentals' },
				...(canPrivate ? [{ icon: 'fas fa-briefcase', name: 'my_private_tours', label: 'Private tour listings', active: section === 'my_private_tours' }] : [])
			]
		},
		{ icon: 'fas fa-wallet', name: 'transactions', label: 'Transactions', value: '', active: section == 'transactions', show: true }
	];
	const sideBarMenu = fullSideBar.filter((item) => item.show !== false);

	const handleNav = (data) => {
		setMobileMenuOpen(false);
		setSection(data);
		if (data === 'my_group_trips') history.push('/partner/group-trips');
		else if (data === 'my_shared_rentals') history.push('/partner/shared-rentals');
		else if (data === 'my_private_tours') history.push('/partner/private-tours');
		else if (data === 'group_tours') history.push('/partner/group_tours?pending=1');
		else if (data === 'shared_rentals') history.push('/partner/shared_rentals?pending=1');
		else if (data === 'packages') history.push('/partner/package_bookings?pending=1');
		else if (data === 'transactions') history.push('/partner/service_transactions');
		else if (data) history.push(`/partner#${data}`);
		else history.push('/partner');
	};

	return (
		<div id='content'>
			{mobileMenuOpen && (
				<div
					className='admin-mobile-backdrop d-md-none'
					onClick={() => setMobileMenuOpen(false)}
					aria-hidden="true"
				/>
			)}
			<div className={`admin-mobile-drawer d-md-none ${mobileMenuOpen ? 'open' : ''}`}>
				<div className='admin-mobile-drawer-header'>
					<span className='admin-mobile-drawer-title'>Vendor menu</span>
					<button
						type='button'
						className='admin-mobile-drawer-close btn btn-link text-white p-2'
						onClick={() => setMobileMenuOpen(false)}
						aria-label='Close menu'
					>
						<i className='fas fa-times' />
					</button>
				</div>
				<div className='admin-mobile-drawer-body'>
					{pendingCounts.group_tours + pendingCounts.shared_rentals > 0 && (
						<div className='partner-sidebar-banner partner-mobile-drawer-banner' role='alert'>
							<div className='partner-sidebar-banner__icon'>
								<i className='fas fa-tasks' aria-hidden='true' />
							</div>
							<div className='partner-sidebar-banner__body'>
								<p className='partner-sidebar-banner__text'>
									You have <strong>{pendingCounts.group_tours + pendingCounts.shared_rentals}</strong> trip{pendingCounts.group_tours + pendingCounts.shared_rentals !== 1 ? 's' : ''} pending completion
								</p>
								<button
									type='button'
									className='partner-sidebar-banner__action'
									onClick={() => { setMobileMenuOpen(false); history.push(pendingCounts.group_tours > 0 ? '/partner/group_tours?pending=1' : '/partner/shared_rentals?pending=1'); }}
								>
									View & complete →
								</button>
							</div>
						</div>
					)}
					<Sidebar items={sideBarMenu} onItemSelect={handleNav} />
				</div>
			</div>
			{showOnboardingBanner && (
				<div className='partner-welcome-banner' role='status'>
					<div className='partner-welcome-banner__inner'>
						<span className='partner-welcome-banner__icon'><i className='fas fa-check-circle' aria-hidden='true' /></span>
						<p className='partner-welcome-banner__text'>You&apos;re now a Visit All Nepal Partner. Our team will contact you shortly for onboarding.</p>
						<button type='button' className='partner-welcome-banner__dismiss' onClick={() => setShowOnboardingBanner(false)} aria-label='Dismiss'>×</button>
					</div>
				</div>
			)}
			<nav className='navbar navbar-expand-lg navbar-dark bg-primary sticky-top'>
				<div className='container align-items-stretch'>
					<div className='navbar-header d-flex align-items-center flex-grow-1'>
						<button
							type='button'
							className='admin-mobile-menu-btn d-flex d-md-none align-items-center justify-content-center btn btn-link text-white p-2 mr-2'
							onClick={() => setMobileMenuOpen(true)}
							aria-label='Open menu'
						>
							<i className='fas fa-bars' style={{ fontSize: '1.25rem' }} />
						</button>
						<Link to='/partner' className='navbar-brand animated bounce delay-2s'>
							<img src={logo} style={{width: "140px"}} alt='Visit All Nepal' />
						</Link>
						{loggedIn && (
							<span className='badge badge-light align-self-center ml-2' title='You are in the Vendor area — your earnings and assignments'>
								Vendor
							</span>
						)}
					</div>

					<div className='navbar-collapse collapse align-items-stretch'>
						<span className='navbar-nav ml-auto align-items-stretch' />
					</div>
					<div className='d-flex align-items-stretch'>
						{loggedIn && (
							<Dropdown icon='icon-user' title={userInitials(currentUser)} className='text-white pl-3'>
								<ul className='text-normal'>
									<li className='m-0'>
										<Link to={`/partner#profile`} className='item text-bold'>
											{t('Profile')}
										</Link>
									</li>
									<li className='m-0'>
										<a className='item text-bold' onClick={logout}>
											Logout
										</a>
									</li>
								</ul>
							</Dropdown>
						)}

						{!loggedIn && (
							<div className='d-flex align-items-center login'>
								<Link to='/login' className='text-bold text-white pl-3'>
									LOGIN
								</Link>
							</div>
						)}
					</div>
				</div>
			</nav>
			<div className='row'>
				<div className='d-none d-md-block col-md-2 p-0 partner-sidebar-col'>
					<div className='partner-sidebar-inner'>
						{pendingCounts.group_tours + pendingCounts.shared_rentals > 0 && (
							<div className='partner-sidebar-banner' role='alert'>
								<div className='partner-sidebar-banner__icon'>
									<i className='fas fa-tasks' aria-hidden='true' />
								</div>
								<div className='partner-sidebar-banner__body'>
									<p className='partner-sidebar-banner__text'>
										You have <strong>{pendingCounts.group_tours + pendingCounts.shared_rentals}</strong> trip{pendingCounts.group_tours + pendingCounts.shared_rentals !== 1 ? 's' : ''} pending completion
									</p>
									<button
										type='button'
										className='partner-sidebar-banner__action'
										onClick={() => history.push(pendingCounts.group_tours > 0 ? '/partner/group_tours?pending=1' : '/partner/shared_rentals?pending=1')}
									>
										View & complete →
									</button>
								</div>
							</div>
						)}
						<Sidebar items={sideBarMenu} onItemSelect={handleNav} />
					</div>
				</div>
				<div className='col-12 col-md-10 p-4' key={location.pathname}>
					{children}
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = ({userStore}) => ({
	currentUser: userStore.currentUser
});

export default connect(mapStateToProps)(PartnerLayout);
